/**
 * DocumentationPage
 *
 * Self-service documentation hub. Renders high-level end-user documentation
 * TOPICS (authored markdown, viewed in-app), the USER GUIDES (view-only) and
 * PLATFORM GUIDES (view + downloadable .md), plus external resources — with a
 * live client-side search across everything.
 *
 * @module help
 * @spec 102-screen-completeness
 */

import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BookOpen,
  Search,
  ExternalLink,
  FileText,
  Code,
  Settings,
  Shield,
  Database,
  Zap,
  Users,
  HelpCircle,
  Download,
  Bell,
  Gauge,
  TrendingUp,
  Boxes,
  RefreshCw,
  Building2,
} from 'lucide-react';
import { supportHelp } from '../../data/help/supportHelp';
import { platformGuides, usageGuides, type PlatformGuide } from '../../data/help/platformGuides';
import { docTopics, type DocTopic } from '../../data/help/docTopics';
import { PageLayout, Card, Button, PageHelpPanel, AppTooltip, EmptyState } from '../../components/ui';

/**
 * Kept for the Feature-041 documentation service/hook types. The hub renders from
 * static content (docTopics/guides), so these are no longer used to drive the page.
 */
export interface DocCategory {
  id: string;
  title: string;
  description: string;
  icon: string;
  articleCount: number;
  articles: DocArticle[];
}

export interface DocArticle {
  id: string;
  title: string;
  description: string;
  lastUpdated: string;
  readTime: number;
}

const ICON_MAP: Record<string, React.ElementType> = {
  'file-text': FileText,
  code: Code,
  settings: Settings,
  shield: Shield,
  database: Database,
  zap: Zap,
  users: Users,
  'help-circle': HelpCircle,
  'book-open': BookOpen,
  bell: Bell,
  gauge: Gauge,
  'trending-up': TrendingUp,
  boxes: Boxes,
  'refresh-cw': RefreshCw,
  'building-2': Building2,
};

/** Does the query match any of the given fields (case-insensitive substring)? */
function matchesQuery(query: string, ...fields: (string | undefined)[]): boolean {
  if (!query) return true;
  const haystack = fields.filter(Boolean).join(' ').toLowerCase();
  return haystack.includes(query);
}

/**
 * In-app references that have no menu entry of their own. Owner rule, 2026-11-01: every screen is reached
 * from the menu or from a screen, so the design-system showcase is reached from here.
 */
const DESIGN_SYSTEM_REFERENCE = {
  path: '/design-system',
  title: 'Design system',
  description: 'The component kit, tokens and layout rules every screen is built from',
};

/** A topic card — links to the in-app viewer, no download. */
const TopicCard: React.FC<{ topic: DocTopic }> = ({ topic }) => {
  const Icon = ICON_MAP[topic.icon] || BookOpen;
  return (
    <Link to={`/help/docs/guide/${topic.slug}`} className="block">
      <Card isHoverable className="h-full transition-all hover:border-[var(--color-accent)]">
        <div className="flex items-start gap-4">
          <div className="rounded-lg bg-[var(--color-bg-subtle)] p-3">
            <Icon className="h-6 w-6 text-[var(--color-accent)]" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-lg font-semibold text-[var(--color-text-primary)]">
              {topic.title}
            </h3>
            <p className="mt-1 line-clamp-2 text-sm text-[var(--color-text-secondary)]">
              {topic.description}
            </p>
          </div>
        </div>
      </Card>
    </Link>
  );
};

/** A guide card — View, plus Download .md when the guide is downloadable. */
const GuideCard: React.FC<{ guide: PlatformGuide }> = ({ guide }) => {
  const isDownloadable = guide.downloadable !== false;
  return (
    <Card className="flex h-full flex-col">
      <div className="flex items-start gap-4">
        <div className="rounded-lg bg-[var(--color-bg-subtle)] p-3">
          <BookOpen className="h-6 w-6 text-[var(--color-accent)]" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-lg font-semibold text-[var(--color-text-primary)]">{guide.title}</h3>
          <p className="mt-1 text-sm text-[var(--color-text-secondary)]">{guide.description}</p>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Link to={`/help/docs/guide/${guide.slug}`}>
          <Button variant="primary" size="sm" leftIcon={<BookOpen className="h-4 w-4" />}>
            View
          </Button>
        </Link>
        {isDownloadable && (
          <a href={`/docs/${guide.file}`} download>
            <Button variant="secondary" size="sm" leftIcon={<Download className="h-4 w-4" />}>
              Download .md
            </Button>
          </a>
        )}
      </div>
    </Card>
  );
};

const SectionHeading: React.FC<{ children: React.ReactNode; first?: boolean }> = ({
  children,
  first,
}) => (
  <h2
    className={`mb-4 text-lg font-semibold text-[var(--color-text-primary)] ${
      first ? '' : 'mt-8 border-t border-[var(--color-border)] pt-8'
    }`}
  >
    {children}
  </h2>
);

export const DocumentationPage: React.FC = () => {
  const [query, setQuery] = useState('');
  const q = query.trim().toLowerCase();

  const topics = useMemo(
    () => docTopics.filter((t) => matchesQuery(q, t.title, t.description, t.keywords)),
    [q],
  );
  const userGuides = useMemo(
    () => usageGuides.filter((g) => matchesQuery(q, g.title, g.description)),
    [q],
  );
  const platform = useMemo(
    () => platformGuides.filter((g) => matchesQuery(q, g.title, g.description)),
    [q],
  );

  const showDesignSystem = matchesQuery(
    q,
    DESIGN_SYSTEM_REFERENCE.title,
    DESIGN_SYSTEM_REFERENCE.description,
  );

  const nothingMatches =
    q.length > 0 &&
    topics.length === 0 &&
    userGuides.length === 0 &&
    platform.length === 0 &&
    !showDesignSystem;

  return (
    <PageLayout
      title="Documentation"
      description="Find guides, tutorials, and reference documentation"
      icon={<BookOpen className="h-6 w-6" />}
      actions={<PageHelpPanel content={supportHelp} buttonSize="sm" />}
    >
      <div data-testid="documentation-page">
        {/* Search */}
        <div className="mb-8">
          <div className="relative max-w-2xl">
            <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[var(--color-text-secondary)]" />
            <input
              type="text"
              placeholder="Search documentation..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label="Search documentation"
              className="w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-card)] py-3 pl-12 pr-4 text-lg text-[var(--color-text-primary)] focus:border-transparent focus:ring-2 focus:ring-[var(--color-accent)]"
            />
          </div>
        </div>

        {nothingMatches ? (
          <EmptyState
            title="No documentation matches your search"
            message={`Nothing found for “${query}”. Try a different term, or clear the search to browse all topics.`}
            icon={<Search className="h-10 w-10" />}
            action={
              <Button variant="secondary" size="sm" onClick={() => setQuery('')}>
                Clear search
              </Button>
            }
          />
        ) : (
          <>
            {/* Browse by topic — high-level end-user overviews */}
            {topics.length > 0 && (
              <div>
                <SectionHeading first>Browse by topic</SectionHeading>
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                  {topics.map((topic) => (
                    <TopicCard key={topic.slug} topic={topic} />
                  ))}
                </div>
              </div>
            )}

            {/* User Guides — end-user usage docs, read in-app only (no download). */}
            {userGuides.length > 0 && (
              <div>
                <SectionHeading first={topics.length === 0}>User Guides</SectionHeading>
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  {userGuides.map((guide) => (
                    <GuideCard key={guide.slug} guide={guide} />
                  ))}
                </div>
              </div>
            )}

            {/* Platform Guides — in-app reference + downloadable .md (Spec 195). */}
            {platform.length > 0 && (
              <div>
                <SectionHeading first={topics.length === 0 && userGuides.length === 0}>
                  Platform Guides
                </SectionHeading>
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  {platform.map((guide) => (
                    <GuideCard key={guide.slug} guide={guide} />
                  ))}
                </div>
              </div>
            )}

            {/* Reference — in-app pages reached from here because the menu does not list them. */}
            {showDesignSystem && (
              <div>
                <SectionHeading
                  first={topics.length === 0 && userGuides.length === 0 && platform.length === 0}
                >
                  Reference
                </SectionHeading>
                <div className="flex flex-wrap gap-4">
                  <AppTooltip title={DESIGN_SYSTEM_REFERENCE.description}>
                    <Link
                      to={DESIGN_SYSTEM_REFERENCE.path}
                      className="flex items-center gap-2 rounded-lg bg-[var(--color-bg-subtle)] px-4 py-2 text-[var(--color-text-secondary)] transition-colors hover:text-[var(--color-text-primary)]"
                    >
                      <Code className="h-4 w-4" aria-hidden="true" />
                      {DESIGN_SYSTEM_REFERENCE.title}
                    </Link>
                  </AppTooltip>
                </div>
              </div>
            )}

            {/* External Resources */}
            <div>
              <SectionHeading>External Resources</SectionHeading>
              <div className="flex flex-wrap gap-4">
                <AppTooltip title="Opens Microsoft Fabric documentation in a new tab">
                  <a
                    href="https://learn.microsoft.com/fabric"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 rounded-lg bg-[var(--color-bg-subtle)] px-4 py-2 text-[var(--color-text-secondary)] transition-colors hover:bg-[var(--color-bg-subtle)]"
                  >
                    Microsoft Fabric Docs
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </AppTooltip>
              </div>
            </div>
          </>
        )}
      </div>
    </PageLayout>
  );
};

export default DocumentationPage;
